import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { customerService } from "../../../../services/api/customerService";
import { customerProfileService } from "../../../../services/api/customerProfileService";
import { reportService } from "../../../../services/api/reportService";
import { downloadPdfBlob } from "../utils/downloadPdfBlob";
import {
  buildOriginalProfileChartParams,
  buildOriginalProfileExportParams,
  buildOriginalProfileListParams,
  formatOriginalProfileRows,
  getOriginalProfileTotal,
  normalizeOriginalProfileChart,
  ORIGINAL_PROFILE_PAGE_SIZE,
  validateDateRange,
} from "../utils/intellectualPropertyReportUtils";

export const useIntellectualPropertyReport = ({ clientId } = {}) => {
  const { token } = useContext(AuthContext);
  const isEmbedded = !!clientId;

  const [selectedClient, setSelectedClient] = useState(
    clientId ? { value: clientId, label: "" } : null,
  );
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerPage, setCustomerPage] = useState(1);
  const [customerTotalPages, setCustomerTotalPages] = useState(1);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [searchCustomerValue, setSearchCustomerValue] = useState("");

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const searchDebounceTimer = useRef(null);
  const requestIdRef = useRef(0);

  const effectiveClientId = selectedClient?.value || clientId || null;

  const totalProfiles = useMemo(
    () => getOriginalProfileTotal(pagination, chartData),
    [pagination, chartData],
  );

  const totalPages = useMemo(
    () => Math.max(1, Number(pagination?.totalPages) || 1),
    [pagination],
  );

  const serviceTypeCount = useMemo(
    () => chartData.filter((item) => Number(item.value || 0) > 0).length,
    [chartData],
  );

  const fetchCustomers = useCallback(
    async (page = 1, searchValue = "", append = false) => {
      if (isEmbedded || !token) return;

      try {
        setLoadingCustomers(true);

        const response = await customerService.get_admin_customers_short(token, {
          page,
          limit: 10,
          search_value: searchValue || undefined,
        });

        if (response?.success && response?.data) {
          const options = response.data.map((customer) => ({
            value: customer.clientId,
            label: customer.fullName,
          }));

          if (append) {
            setCustomerOptions((prev) => [...prev, ...options]);
          } else {
            setCustomerOptions([
              { value: null, label: "-- Tất cả khách hàng --" },
              ...options,
            ]);
          }

          setCustomerTotalPages(response.totalPages || 1);
        }
      } catch (error) {
        console.error("Lỗi lấy danh sách khách hàng:", error);
        toast.error("Không thể tải danh sách khách hàng");
      } finally {
        setLoadingCustomers(false);
      }
    },
    [isEmbedded, token],
  );

  const fetchReports = useCallback(
    async (page = 1, append = false, isFilter = false) => {
      if (!token) return;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      try {
        if (append) {
          setLoadingMore(true);
        } else if (isFilter) {
          setTableLoading(true);
        } else {
          setInitialLoading(true);
        }

        const listParams = buildOriginalProfileListParams({
          page,
          limit: ORIGINAL_PROFILE_PAGE_SIZE,
          search,
          fromDate,
          toDate,
          clientId: effectiveClientId,
        });

        if (append) {
          const listResponse = await customerProfileService.get_original_profiles(
            token,
            listParams,
          );

          if (requestId !== requestIdRef.current) return;

          const nextRows = formatOriginalProfileRows({
            profiles: listResponse?.data || [],
            currentPage: page,
            pageSize: ORIGINAL_PROFILE_PAGE_SIZE,
          });

          setTableData((prev) => [...prev, ...nextRows]);
          setPagination(listResponse?.pagination || null);
          setHasMore(
            Number(listResponse?.pagination?.currentPage || page) <
            Number(listResponse?.pagination?.totalPages || page),
          );
          return;
        }

        const chartParams = buildOriginalProfileChartParams({
          fromDate,
          toDate,
          clientId: effectiveClientId,
        });

        const [chartResponse, listResponse] = await Promise.all([
          reportService.get_report_original_profile(token, chartParams),
          customerProfileService.get_original_profiles(token, listParams),
        ]);

        if (requestId !== requestIdRef.current) return;

        const nextChartData = normalizeOriginalProfileChart(
          chartResponse?.chart || [],
        );
        const nextRows = formatOriginalProfileRows({
          profiles: listResponse?.data || [],
          currentPage: page,
          pageSize: ORIGINAL_PROFILE_PAGE_SIZE,
        });

        setChartData(nextChartData);
        setTableData(nextRows);
        setPagination(listResponse?.pagination || null);
        setHasMore(
          Number(listResponse?.pagination?.currentPage || page) <
          Number(listResponse?.pagination?.totalPages || page),
        );
      } catch (error) {
        console.error("Lỗi lấy danh mục tài sản trí tuệ:", error);
        toast.error(
          error.response?.data?.message ||
          "Không thể tải danh mục tài sản trí tuệ",
        );
      } finally {
        if (requestId === requestIdRef.current) {
          setInitialLoading(false);
          setTableLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [effectiveClientId, fromDate, search, toDate, token],
  );

  const handleApplyFilter = useCallback(() => {
    const dateError = validateDateRange(fromDate, toDate);

    if (dateError) {
      toast.error(dateError);
      return;
    }

    setCurrentPage(1);
    fetchReports(1, false, true);
  }, [fetchReports, fromDate, toDate]);

  const handleLoadMoreReports = useCallback(() => {
    if (!hasMore || loadingMore || tableLoading) return;

    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchReports(nextPage, true, false);
  }, [currentPage, fetchReports, hasMore, loadingMore, tableLoading]);

  const handleReportPageChange = useCallback(
    (page) => {
      const nextPage = Number(page);
      if (!Number.isFinite(nextPage) || nextPage < 1 || nextPage > totalPages) {
        return;
      }

      if (nextPage === currentPage) {
        return;
      }

      setCurrentPage(nextPage);
      fetchReports(nextPage, false, true);
    },
    [currentPage, fetchReports, totalPages],
  );

  const handleExportPdf = useCallback(async () => {
    const dateError = validateDateRange(fromDate, toDate);

    if (dateError) {
      toast.error(dateError);
      return;
    }

    try {
      setExporting(true);

      const params = buildOriginalProfileExportParams({
        search,
        fromDate,
        toDate,
        clientId: effectiveClientId,
      });

      const response = await reportService.export_original_profile_pdf(
        token,
        params,
      );

      downloadPdfBlob(
        response,
        `Danh-muc-tai-san-tri-tue-${Date.now()}.pdf`,
      );
      toast.success("Xuất file PDF thành công!");
    } catch (error) {
      console.error("Lỗi xuất file danh mục tài sản trí tuệ:", error);
      toast.error(
        error.response?.data?.message || error.message || "Xuất PDF thất bại",
      );
    } finally {
      setExporting(false);
    }
  }, [effectiveClientId, fromDate, search, toDate, token]);

  const handleLoadMoreCustomers = useCallback(() => {
    if (customerPage >= customerTotalPages || loadingCustomers) return;

    const nextPage = customerPage + 1;
    setCustomerPage(nextPage);
    fetchCustomers(nextPage, searchCustomerValue, true);
  }, [
    customerPage,
    customerTotalPages,
    fetchCustomers,
    loadingCustomers,
    searchCustomerValue,
  ]);

  const handleSearchCustomer = useCallback(
    (value) => {
      setSearchCustomerValue(value);

      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }

      searchDebounceTimer.current = setTimeout(() => {
        setCustomerPage(1);
        fetchCustomers(1, value, false);
      }, 500);
    },
    [fetchCustomers],
  );

  const handleCustomerDropdownOpen = useCallback(() => {
    if (!customerOptions.length) {
      fetchCustomers(1, "", false);
    }
  }, [customerOptions.length, fetchCustomers]);

  useEffect(() => {
    return () => {
      if (searchDebounceTimer.current) {
        clearTimeout(searchDebounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchReports(1, false, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    chartData,
    currentPage,
    customerOptions,
    customerPage,
    customerTotalPages,
    exporting,
    fromDate,
    hasMore,
    initialLoading,
    isEmbedded,
    loadingCustomers,
    loadingMore,
    search,
    selectedClient,
    serviceTypeCount,
    tableData,
    tableLoading,
    toDate,
    totalPages,
    totalProfiles,
    visibleProfiles: tableData.length,
    handleApplyFilter,
    handleCustomerDropdownOpen,
    handleExportPdf,
    handleLoadMoreCustomers,
    handleLoadMoreReports,
    handleReportPageChange,
    handleSearchCustomer,
    setFromDate,
    setSearch,
    setSelectedClient,
    setToDate,
  };
};
